import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import PrintIcon from "@material-ui/icons/Print";
import ApiClient from "utils/ApiClient";

// ─── Column mapping ────────────────────────────────────────────────────────────
const COL = {
  sku: "column_901",
  datetime: "column_900",
  riceType: "column_902",
  bagSize: "column_903",
  brandSkuCode: "column_904",
  brandName: "column_909",
  variety: "column_905",
  sourceBunker: "column_906",
  sourceBatchId: "column_907",
  bagsQty: "column_908",
  inwardType: "column_910",
  packingRef: "column_911",
};

// ─── The 6 fixed sections (always rendered, left to right) ────────────────────
const SECTIONS = [
  { bagSize: "26 KG", riceType: "Rice",           label: "26 KG RICE",            headerBg: "#1565c0", headerText: "#fff" },
  { bagSize: "50 KG", riceType: "Rice",           label: "50 KG RICE",            headerBg: "#0d47a1", headerText: "#fff" },
  { bagSize: "26 KG", riceType: "Broken Rice",    label: "26 KG BROKEN RICE",     headerBg: "#e65100", headerText: "#fff" },
  { bagSize: "50 KG", riceType: "Broken Rice",    label: "50 KG BROKEN RICE",     headerBg: "#bf360c", headerText: "#fff" },
  { bagSize: "26 KG", riceType: "Rejection Rice", label: "26 KG REJECTION RICE",  headerBg: "#880e4f", headerText: "#fff" },
  { bagSize: "50 KG", riceType: "Rejection Rice", label: "50 KG REJECTION RICE",  headerBg: "#4a148c", headerText: "#fff" },
];

// ─── Date age color ────────────────────────────────────────────────────────────
function getDateStyle(datetimeStr) {
  if (!datetimeStr) return { color: "#546e7a", bg: "transparent" };
  const dt = new Date(datetimeStr);
  if (isNaN(dt.getTime())) return { color: "#546e7a", bg: "transparent" };
  const diffDays = (new Date() - dt) / (1000 * 60 * 60 * 24);
  if (diffDays < 0)  return { color: "#fff", bg: "#1565c0" };  // future date → blue
  if (diffDays > 20) return { color: "#fff", bg: "#212121" };   // >20 days old → black
  if (diffDays > 15) return { color: "#fff", bg: "#c62828" };   // >15 days old → red
  if (diffDays > 13) return { color: "#212121", bg: "#f9a825" };// >13 days old → yellow
  return               { color: "#fff", bg: "#2e7d32" };        // ≤13 days old → green
}

// ─── Inward type short label ──────────────────────────────────────────────────
function inwardShortLabel(type) {
  if (!type) return "";
  const map = { Packing: "P", "Transfer In": "T", Adjustment: "A" };
  return map[type] || type[0];
}

function inwardBadgeStyle(type) {
  const map = {
    Packing:      { bg: "#e8f5e9", color: "#2e7d32" },
    "Transfer In":{ bg: "#fff3e0", color: "#e65100" },
    Adjustment:   { bg: "#fce4ec", color: "#c62828" },
  };
  return map[type] || { bg: "#eceff1", color: "#546e7a" };
}

// ─── Format helpers ────────────────────────────────────────────────────────────
function fmtDate() {
  return new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtFullDate() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function fmtDateShort(datetimeStr) {
  if (!datetimeStr) return "—";
  const dt = new Date(datetimeStr);
  if (isNaN(dt.getTime())) return datetimeStr;
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTimeFull(datetimeStr) {
  if (!datetimeStr) return "—";
  const dt = new Date(datetimeStr);
  if (isNaN(dt.getTime())) return datetimeStr;
  return dt.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// StockTable — one section column
// ═══════════════════════════════════════════════════════════════════════════════
function StockTable({ section, rows }) {
  const hasRows = rows && rows.length > 0;
  const totalBags = hasRows
    ? rows.reduce((sum, r) => sum + (parseInt(r[COL.bagsQty], 10) || 0), 0)
    : 0;

  return (
    <div
      className="rss-stock-table"
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 360,
        maxWidth: 400,
        flexShrink: 0,
        background: "#fff",
        borderRadius: 8,
        overflow: "hidden",
        border: `2px solid ${section.headerBg}`,
        boxShadow: `0 2px 10px ${section.headerBg}25`,
      }}
    >
      {/* Section header */}
      <div
        style={{
          background: section.headerBg,
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: section.headerText,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
          }}
        >
          {section.label}
        </span>
        {hasRows && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              background: "rgba(0,0,0,0.2)",
              borderRadius: 20,
              padding: "1px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {totalBags} bags
          </span>
        )}
      </div>

      {/* Column headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "90px 32px 26px 56px 1fr 1fr",
          background: "#263238",
          padding: "5px 8px",
          gap: 6,
        }}
      >
        {["DATE", "PS", "S", "BAGS", "QUALITY", "BRAND"].map((h) => (
          <span
            key={h}
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#eceff1",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {h}
          </span>
        ))}
      </div>

      {/* Rows */}
      <div className="rss-stock-table-rows" style={{ flex: 1, overflowY: "auto", maxHeight: 480 }}>
        {!hasRows ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              gap: 8,
              opacity: 0.5,
            }}
          >
            <span style={{ fontSize: 24 }}>📭</span>
            <span
              style={{
                fontSize: 12,
                color: "#90a4ae",
                fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
              }}
            >
              No data
            </span>
          </div>
        ) : (
          rows.map((row, i) => {
            const ds = getDateStyle(row[COL.datetime]);
            return (
              <div
                key={row.row_id || i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 32px 26px 56px 1fr 1fr",
                  padding: "5px 8px",
                  gap: 6,
                  background: i % 2 === 0 ? "#fff" : "#f8fafc",
                  borderBottom: "1px solid #eceff1",
                  alignItems: "center",
                  transition: "background 0.12s",
                  cursor: "default",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#f8fafc"; }}
              >
                {/* DATE */}
                <Tooltip title={fmtDateTimeFull(row[COL.datetime])} arrow placement="top">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "2px 5px",
                      borderRadius: 4,
                      background: ds.bg,
                      color: ds.color,
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fmtDateShort(row[COL.datetime])}
                  </span>
                </Tooltip>

                {/* PS - TBD: confirm with client */}
                <span style={{ fontSize: 10, color: "#b0bec5", fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" }}>—</span>

                {/* S - TBD: confirm with client */}
                <span style={{ fontSize: 10, color: "#b0bec5", fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" }}>—</span>

                {/* BAGS */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#e8eaf6",
                    border: "1.5px solid #3949ab30",
                    borderRadius: 6,
                    padding: "2px 4px",
                    minWidth: 48,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 900,
                      color: "#1a237e",
                      lineHeight: 1.2,
                      fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
                      letterSpacing: -0.5,
                    }}
                  >
                    {row[COL.bagsQty] || "—"}
                  </span>
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 600,
                      color: "#5c6bc0",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
                    }}
                  >
                    bags
                  </span>
                </div>

                {/* QUALITY / Variety */}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#37474f",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
                  }}
                  title={row[COL.variety] || ""}
                >
                  {row[COL.variety] || "—"}
                </span>

                {/* BRAND */}
                <div style={{ overflow: "hidden" }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#37474f",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
                    }}
                    title={row[COL.brandName] || ""}
                  >
                    {row[COL.brandName] || "—"}
                  </div>
                  {row[COL.brandSkuCode] && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "#90a4ae",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row[COL.brandSkuCode]}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer: total */}
      {hasRows && (
        <div
          style={{
            padding: "5px 10px",
            background: `${section.headerBg}15`,
            borderTop: `2px solid ${section.headerBg}40`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: "#78909c",
              fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
            }}
          >
            {rows.length} entries
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: section.headerBg,
              fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
            }}
          >
            Total: {totalBags} bags
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Date Legend ──────────────────────────────────────────────────────────────
function DateLegend() {
  const items = [
    { label: "Future date", bg: "#1565c0", color: "#fff" },
    { label: "≤ 13 days",   bg: "#2e7d32", color: "#fff" },
    { label: "> 13 days",   bg: "#f9a825", color: "#212121" },
    { label: "> 15 days",   bg: "#c62828", color: "#fff" },
    { label: "> 20 days",   bg: "#212121", color: "#fff" },
  ];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "5px 12px",
        background: "rgba(255,255,255,0.9)",
        borderRadius: 6,
        border: "1px solid #e0e0e0",
        marginBottom: 10,
        fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: 0.5 }}>
        Date colour:
      </span>
      {items.map((item) => (
        <span key={item.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "#546e7a" }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, background: item.bg, display: "inline-block", flexShrink: 0 }} />
          {item.label}
        </span>
      ))}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function RiceStockSummaryReport({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [reportDate, setReportDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReportDate(fmtFullDate());
      const result = await ApiClient.Views.getViewData({
        list_id: 27109950,
        view_id: 226,
        limit: 100000,
      });
      setRows(result?.data?.data || []);
    } catch (err) {
      console.error("Rice Stock Summary fetch error:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  // Build lookup: "26 KG|Rice" → rows[]
  const dataMap = {};
  rows.forEach((row) => {
    const key = `${row[COL.bagSize] || ""}|${row[COL.riceType] || ""}`;
    if (!dataMap[key]) dataMap[key] = [];
    dataMap[key].push(row);
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          width: "98vw",
          maxWidth: "98vw",
          background: "#f0f4f8",
          borderRadius: 12,
          maxHeight: "96vh",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        },
      }}
    >
      <style>{`
        .rss-no-print .MuiIconButton-root {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .rss-no-print .MuiIconButton-root:hover {
          background-color: rgba(255, 255, 255, 0.15) !important;
        }
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          .rss-no-print { display: none !important; }
          .MuiDialog-root .MuiBackdrop-root { display: none !important; }
          .MuiDialog-paper {
            box-shadow: none !important; border-radius: 0 !important;
            max-height: none !important; margin: 0 !important;
            width: 100% !important; max-width: 100% !important;
            overflow: visible !important;
          }
          .MuiDialogContent-root {
            overflow: visible !important;
            padding: 6px !important;
          }
          .rss-tables-container {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            overflow: visible !important;
            flex-direction: unset !important;
          }
          .rss-stock-table {
            min-width: unset !important;
            max-width: unset !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .rss-stock-table-rows {
            max-height: none !important;
            overflow: visible !important;
          }
        }
      `}</style>

      {/* ── Title Bar ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "12px 12px 0 0",
          boxShadow: "0 2px 8px rgba(26,35,126,0.3)",
          flexShrink: 0,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#fff",
              letterSpacing: 0.5,
              textShadow: "0 1px 3px rgba(0,0,0,0.2)",
              fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
            }}
          >
            Finished Goods Daily Stock Report
            <span style={{ color: "#9fa8da", fontSize: 13, fontWeight: 400, marginLeft: 10 }}>
              MS Agro Industries
            </span>
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2, fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" }}>
            As on: {fmtDate()} &nbsp;·&nbsp; {rows.length} records loaded
          </div>
        </div>
        <div className="rss-no-print" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              <RefreshIcon fontSize="small" style={{ color: '#fff' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton size="small" onClick={() => window.print()}>
              <PrintIcon fontSize="small" style={{ color: '#fff' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" style={{ color: '#fff' }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Body ── */}
      <DialogContent style={{ padding: "12px 14px", overflow: "auto", display: "flex", flexDirection: "column" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 16 }}>
            <CircularProgress size={44} style={{ color: "#3949ab" }} />
            <span style={{ fontSize: 14, color: "#78909c", fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" }}>
              Loading rice stock data…
            </span>
          </div>
        ) : error ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 12 }}>
            <span style={{ fontSize: 36 }}>⚠️</span>
            <span style={{ fontSize: 14, color: "#c62828", fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" }}>{error}</span>
          </div>
        ) : (
          <>
            <DateLegend />

            {/* ── 6 tables side by side, scroll right on screen / 3-col grid on print ── */}
            <div
              className="rss-tables-container"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 12,
                overflowX: "auto",
                overflowY: "hidden",
                paddingBottom: 8,
                alignItems: "flex-start",
              }}
            >
              {SECTIONS.map((section) => {
                const key = `${section.bagSize}|${section.riceType}`;
                const sectionRows = dataMap[key] || [];
                return (
                  <StockTable
                    key={key}
                    section={section}
                    rows={sectionRows}
                  />
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RiceStockSummaryReport;
